import React, { useEffect, useState } from 'react';
import { homeSetupAPI, customerSupportAPI } from '../../../../services/redwolfSdkAPI';
import Modal from '../../../common/components/Modal';
import * as Sentry from '@sentry/react';
import { useGoogleAnalytics } from '../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import Spinner from '../../../common/components/Spinner';
import { dateFormat, notification } from '../../../../utils/constants';
import { openNotification } from '../../../../store/commonSlice';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import AddDevice from './AddDevice';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../utils/localStorage';
import { getProductImage } from '../../../../utils/commonFunctions';
import useGetProductImage from '../../../common/hooks/useGetProductImage';
import TableSkeleton from '../../../common/skeletons/TableSkeleton';
import Select from 'react-select';
import { reactDropDownStyles } from '../../../../utils/constants';

import WebSupport from '../WebSupport';
interface SupportUpdateModalProps {
	isWebSupportModalOpen: boolean;
	serialNumberShow: boolean;
	updateSupportModalToggle: (
		isUpdateSupportModalOpen: boolean,
		isUpdateSerialNumber: boolean
	) => void;
	callbackTvs: (payload: any) => void;
}

const options = [
	{ value: 'firmware', label: 'Firmware' },
	{ value: 'usermanual', label: 'User Manual' },
	{ value: 'support', label: 'Support' },
	{ value: 'techspecs', label: 'Device Specs' },
	{ value: 'remove', label: 'Remove' }
];

const Devices = ({
	isWebSupportModalOpen,
	serialNumberShow,
	updateSupportModalToggle,
	callbackTvs
}: SupportUpdateModalProps) => {
	const [isRemoveTvInfoModalOpen, setIsRemoveTvInfoModalOpen] = useState(false);
	const [isAddyourTvsInfoModalOpen, setIsAddyourTvsInfoModalOpen] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [deviceList, setDeviceList]: any = useState([]);
	const [deviceId, setDeviceId]: any = useState('');
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [productImages, setProductImages] = useState([]);
	const [fetchProductImage] = useGetProductImage();
	const [isSupportModalOpen, setSupportModalOpen] = useState(false);
	const [isModalDeviceModalNumber, setModalDeviceModalNumber] = useState<string | null>(null);
	const [isModalDeviceSerialNumber, setModalDeviceSerialNumber] = useState<string | null>(null);
	const [modalVisible, setModalVisible] = useState(false);
	const [deviceItem, setDeviceItem] = useState({});

	const transaction = Sentry.startInactiveSpan({ name: 'Devices' });

	const dispatch = useDispatch();
	const token = getToken();

	const closeModal = () => {
		setModalVisible(false);
		setSupportModalOpen(false);
		updateSupportModalToggle(false, true);
	};

	useEffect(() => {
		getDeviceList();
	}, []);

	const handleTypeSelect = (event: any, card: any, index: any) => {
		const existingDeviceList = [...deviceList];
		existingDeviceList[index]['selectedOptionValue'] = event.value;
		setDeviceList(existingDeviceList);

		setDeviceItem(card);
		const modelNumber = card.modelName;
		if (event.value === 'support') {
			setModalVisible(true);
			//setSupportModalOpen(true);
			setModalDeviceModalNumber(card.modelName);
			setModalDeviceSerialNumber(card.serialNumber);
		} else if (event.value === 'firmware') {
			getProductAssets(modelNumber, 'firmware');
		} else if (event.value === 'techspecs') {
			getProductAssets(modelNumber, 'techspecs');
		} else if (event.value === 'usermanual') {
			getProductAssets(modelNumber, 'usermanual');
		} else if (event.value === 'remove') {
			handleRemoveDevice(card, event);
		}
	};

	const getDeviceList = () => {
		setIsSkeletonLoading(true);
		const filter = window.pay.com.vizio.pay.api.JsLinkedDevicesFilter.Pay.toSDKFilter();

		requestRedwolfService(homeSetupAPI.getLinkedDevices(token, filter))
			.then((sdkResponse: any) => {
				if (
					sdkResponse.constructor.$metadata$.simpleName === 'LinkedDevicesResult' &&
					sdkResponse.response
				) {
					const productIds: any = [];
					let deviceList: any = [];
					if (sdkResponse.response && sdkResponse.response.devices) {
						deviceList = sdkResponse.response.devices.toArray();
					}
					deviceList.forEach((product: any) => {
						productIds.push(product.modelName);
					});
					if (productIds && productIds.length > 0) {
						fetchProductImage(productIds)
							.then((response: any) => {
								if (response) {
									setProductImages(response);
									const exitingDeviceList = deviceList.map((v: any) => ({
										...v,
										isActive: false,
										selectedOptionValue: 'Info'
									}));
									setDeviceList(exitingDeviceList);

									setIsSkeletonLoading(false);
								}
							})
							.catch((err: any) => {
								const exitingDeviceList = deviceList.map((v: any) => ({
									...v,
									isActive: false,
									selectedOptionValue: 'Info'
								}));
								setDeviceList(exitingDeviceList);
								setIsSkeletonLoading(false);
								Sentry.captureException(err);
							});
					} else {
						const exitingDeviceList = deviceList.map((v: any) => ({
							...v,
							isActive: false,
							selectedOptionValue: 'Info'
						}));
						setDeviceList(exitingDeviceList);
						setIsSkeletonLoading(false);
					}
				} else {
					setIsSkeletonLoading(false);
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSkeletonLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleRemoveDevice = (card: any, event: any) => {
		setIsRemoveTvInfoModalOpen(true);
		setDeviceId(card.deviceId);
		setModalDeviceModalNumber(null);
		setModalDeviceSerialNumber(null);
	};

	const handleAddDevice = (isNewDeviceAdded: boolean) => {
		if (isNewDeviceAdded) {
			getDeviceList();
		}
	};

	const removeTvDevice = (event: any) => {
		event.preventDefault();
		setIsSpinnerLoading(true);
		requestRedwolfService(homeSetupAPI.unlink(deviceId))
			.then((response: any) => {
				setIsSpinnerLoading(false);
				if (response.constructor.$metadata$.simpleName === 'UnregisterAllResult') {
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: notification.message.deviceUnLinkedSuccess
						})
					);
					setDeviceList([]);
					getDeviceList();
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UNLINK_SUCCESS');
					setIsRemoveTvInfoModalOpen(false);
				} else {
					setIsRemoveTvInfoModalOpen(false);

					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UNLINK_FAILURE', {
						error: response.constructor.$metadata$.simpleName
					});
					Sentry.captureException(new Error('unlink:' + response));
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsRemoveTvInfoModalOpen(false);
				transaction && transaction.end();
				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UNLINK_FAILURE', { error: err.message });
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const renderImage = (modelName: string) => {
		const defaultProductImage = 'images/vizio-logo-print.svg';
		const imageList: any = productImages.find((product: any) => product.id === modelName);
		const imageURL = imageList && getProductImage(imageList.images);
		return imageURL ? imageURL : defaultProductImage;
	};

	const onDropdownKeyDown = (idx: any) => (event: any) => {
		if (event.code === 'Enter') {
			const existingDeviceList = [...deviceList];
			existingDeviceList[idx]['isActive'] = true;
			setDeviceList(existingDeviceList);
		}
	};

	const onDropdownMenuClose = (idx: any) => () => {
		const existingDeviceList = [...deviceList];
		existingDeviceList[idx]['isActive'] = false;
		setDeviceList(existingDeviceList);
	};

	const onDropdownMenuOpen = (idx: any) => () => {
		const existingDeviceList = [...deviceList];
		existingDeviceList[idx]['isActive'] = true;
		setDeviceList(existingDeviceList);
	};

	const getProductAssets = (modelNumberRes: any, assetTypeString: any) => {
		requestRedwolfService(customerSupportAPI.getProductAssets(modelNumberRes))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
					let productAsset: any = [];
					if (response && response.data) {
						productAsset = response.data.toArray();
					}
					if (productAsset.length === 0) {
						dispatch(
							openNotification({
								type: 'ERROR',
								heading: 'Error',
								body: 'Asset types are not available for this product.'
							})
						);
					}
					if (productAsset.length > 0) {
						let attributeKey: any = '';
						let labelKey: any = '';
						if (assetTypeString === 'firmware') {
							attributeKey = 'product-firmware-page';
							labelKey = 'Firmware';
						} else if (assetTypeString === 'usermanual') {
							attributeKey = 'user-manual';
							labelKey = 'User Manual';
						} else if (assetTypeString === 'techspecs') {
							attributeKey = 'product-support-page';
							labelKey = 'Device Specs';
						}
						const found = productAsset.find((obj: any) => {
							return obj.assetType === attributeKey;
						});
						if (found) {
							window.open(found.url, '_blank');
						} else {
							dispatch(
								openNotification({
									type: 'ERROR',
									heading: 'Error',
									body: `${labelKey} not available for this product`
								})
							);
						}
					}
				} else {
					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: 'Unable to process your request. Please try again later.'
						})
					);
				}
			})
			.catch((err: any) => {
				showTechnicalErrorMessage();
			});
	};

	callbackTvs(deviceList.length);

	return (
		<>
			<AddDevice isDeviceAdded={handleAddDevice} />
			{/* Device already linked Modal Popup */}
			<Modal
				containerId="LinkTvOverlay"
				overlaycontainerClass="updateCard"
				show={isAddyourTvsInfoModalOpen}
				onHide={() => setIsAddyourTvsInfoModalOpen(false)}
				heading="Add your TV">
				<form className="edit-modal-form">
					<p className="addtvtext">
						Another VIZIO Account is already active on your TV. Do you want to switch to this
						account?
					</p>
					<p className="addtvtext">
						If you’re using Amazon Alexa or Google Assistant with this TV, you may need to authorize
						each service again using this VIZIO Account.
					</p>
					<p className="addtvtext">
						To view the account assosiated with this device, press the Home button, navigate to
						Extras and highlight VIZIO Account.
					</p>
					<div className="button-field-group-tv">
						<button
							type="submit"
							className="secondary-button desktop-btn"
							id="tv-sign-out"
							aria-label="Sign out">
							Sign out
						</button>
						<button
							type="submit"
							className="primary-button"
							id="unlink-AddTv"
							aria-label="Add TV to list">
							Add tv
						</button>
						<button
							type="submit"
							className="secondary-button mobile-btn"
							id="tv-sign-out"
							aria-label="Sign out">
							Sign out
						</button>
					</div>
				</form>
			</Modal>
			<div id="linked-devices-list">
				{isSkeletonLoading ? (
					<TableSkeleton />
				) : deviceList.length === 0 ? (
					<span id="no-linked-devices">
						Have a VIZIO TV? Add it to your VIZIO Account to easily view information and receive
						personalized support for your devices.
					</span>
				) : (
					<div className="subs-table  add-tv-list" id="linkTvTable">
						<table className="table subscrip-table">
							<thead>
								<tr>
									<th>Product Name</th>
									<th>Model</th>
									<th>Link Date</th>
									<th>More</th>
								</tr>
							</thead>

							<tbody>
								{deviceList.map((card: any, index: number) => {
									return (
										<tr key={index}>
											<td className="app-td">
												<div className="icon-title">
													<div className="sapp-icon brand-img">
														<img src={renderImage(card.modelName)} alt="Product Logo" />
													</div>
													<div className="sapp-title" id={`remove-tv-${index}`}>
														<h3 id={`product-info-${index}`}>{card.friendlyName}</h3>
													</div>
												</div>
											</td>

											<td className="app-td desk">
												<span className="modal-no">{card.modelName}</span>
											</td>

											<td className="app-td desk">
												<span className="date">
													{card.vizioPayData && card.vizioPayData.linkedAt
														? format(new Date(card.vizioPayData.linkedAt), dateFormat)
														: ''}
												</span>
											</td>

											<td className="app-td drop_down">
												<Select
													styles={reactDropDownStyles}
													key={index}
													isSearchable={false}
													getOptionLabel={(option) => option.label}
													options={options}
													onChange={(event) => handleTypeSelect(event, card, index)}
													onKeyDown={onDropdownKeyDown(index)}
													onMenuOpen={onDropdownMenuOpen(index)}
													onMenuClose={onDropdownMenuClose(index)}
													menuIsOpen={card.isActive}
													placeholder="Info"
													aria-labelledby={card.selectedOptionValue}
													value={options.filter(({ value }) => value === card.selectedOptionValue)}
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>
			{/* Unlink TV Modal */}
			<Modal
				containerId="unlinkTvCard"
				show={isRemoveTvInfoModalOpen}
				onHide={() => setIsRemoveTvInfoModalOpen(false)}
				heading="Remove your TV"
				containerClasses="support-modal">
				<>
					<div className="removesubheadbottom">
						<p>
							Removing the TV from your VIZIO Account will disable new purchases. You will continue
							to be charged for subscriptions on scheduled billing dates.
						</p>
					</div>

					<form className="edit-modal-form removeTvForm" id="unlinkDevice" method="POST">
						<div className="form-field content-right">
							<button type="submit" className="primary-button" onClick={removeTvDevice}>
								Remove TV
							</button>
						</div>
					</form>

					{isSpinnerLoading && <Spinner />}
				</>
			</Modal>
			<WebSupport
				modalVisible={modalVisible}
				deviceData={deviceItem}
				isWebSupportModalOpen={isWebSupportModalOpen}
				isModalDeviceModalNumber={isModalDeviceModalNumber}
				isModalDeviceSerialNumber={isModalDeviceSerialNumber}
				closeModal={closeModal}
				updateSupportModalToggle={updateSupportModalToggle}
				serialNumberShow={serialNumberShow}
			/>
		</>
	);
};

export default Devices;
